// CallBackPopup.jsx
import { useContext, useEffect, useMemo, useState } from 'react';
import './CallBackPopup.css';
import { ClosePopupIcon } from '../../assets/icons/icons';
import Popup from '../Popup/Popup';
import { ConfigContext } from '../../assets/contexts/configContext';
import validator from "validator";
import Input from '../Input/Input';
import mainApi from '../../assets/api/MainApi';
import { CONTACT_PHONE, CONTACT_PHONE_2, NO_DELIVERY_TYPE } from '../../assets/utils/constants';
import { motion } from 'framer-motion';
import { CallBackContext } from '../../assets/contexts/callBackContext';
import moment from 'moment/moment';

/** ===== Time / Date rules ===== */
const USER_TZ_OFFSET_HOURS = 4;   // показываем пользователю в UTC+4
const LOW_STOCK_THRESHOLD = 4

/** ===== utils ===== */
const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

// "YYYY-MM-DD" из Date (UTC)
function ymdUTC(d) {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// epoch ms для UTC-наивного "YYYY-MM-DD" + "HH:mm"
function epochMsFromUTC(dateStr, timeStr) {
  const [Y, M, D] = dateStr.split('-').map(Number);
  const [h, m] = timeStr.split(':').map(Number);
  return Date.UTC(Y, M - 1, D, h, m, 0, 0);
}

// конвертация UTC времени в подпись UTC+4 (с сохранением минут)
function utcTimeToUserLabel(hUTC, mUTC = 0) {
  let h = (hUTC + USER_TZ_OFFSET_HOURS) % 24;
  if (h < 0) h += 24;
  return `${pad(h)}:${pad(mUTC)}`;
}

// заголовок месяца (UTC)
function monthTitleUTC(y, m) {
  return new Date(Date.UTC(y, m, 1)).toLocaleString('en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// мета для месячной сетки (UTC)
function getMonthMetaUTC(viewYear, viewMonth) {
  const firstUTC = new Date(Date.UTC(viewYear, viewMonth, 1));
  const firstDay = firstUTC.getUTCDay(); // 0=Sun..6=Sat
  const weekdayMonFirst = firstDay === 0 ? 7 : firstDay; // делаем понедельник первым
  const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate();
  return { weekdayMonFirst, daysInMonth };
}

// appointment_date без таймзоны
function buildAppointmentDateUTC(dateStr, timeStr) {
  return `${dateStr}T${timeStr}:00`;
}

export default function CallBackPopup() {
  const { isCallBackPopupOpen: isOpen, setCallBackPopupOpen: setOpen } = useContext(CallBackContext);
  const { initialConstants } = useContext(ConfigContext) || {};
  const { DELIVERIES } = initialConstants
  const [correctSending, setCorrectSending] = useState(false);

  /** ===== Stepper ===== */
  const [step, setStep] = useState(1); // 1 personal, 2 date, 3 time

  /** ===== Personal ===== */
  const [personal, setPersonal] = useState({ first_name: '', phone: '', email: '' });
  const [personalValidity, setPersonalValidity] = useState({
    phone: { validState: false, errorMessage: '' },
    email: { validState: false, errorMessage: '' },
  });

  function handleChange(e) {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (!digits) {
        setPersonal(p => ({ ...p, phone: '' }));
        setPersonalValidity(v => ({ ...v, phone: { validState: false, errorMessage: '' } }));
        return;
      }
      const formatted = '+' + digits.substring(0, 16);
      const valid = digits.length >= 11;
      setPersonal(p => ({ ...p, phone: formatted }));
      setPersonalValidity(v => ({ ...v, phone: { validState: valid, errorMessage: '' } }));
      return;
    }
    if (name === 'email') {
      const valid = !!value && validator.isEmail(value);
      setPersonal(p => ({ ...p, email: value }));
      setPersonalValidity(v => ({ ...v, email: { validState: valid, errorMessage: '' } }));
      return;
    }
    if (name === 'first_name') setPersonal(p => ({ ...p, first_name: value }));
  }

  function handlePhoneDelete(e) {
    if (e.keyCode === 8 && e.target.value.replace(/\D/g, '').length <= 11) {
      setPersonalValidity(prev => ({ ...prev, phone: { validState: false, errorMessage: '' } }));
    }
  }

  /** ===== Calendar state (UTC) ===== */
  const nowMs = Date.now();
  const todayUTC = ymdUTC(new Date());
  const [selectedDateUTC, setSelectedDateUTC] = useState(''); // YYYY-MM-DD
  const [selectedTimeUTC, setSelectedTimeUTC] = useState(''); // HH:mm

  // Даты и остатки с бека
  const [availableDateSet, setAvailableDateSet] = useState(() => new Set()); // Set<YYYY-MM-DD>
  const [remainByDate, setRemainByDate] = useState({}); // { 'YYYY-MM-DD': number }

  // вью месяца
  const init = new Date();
  const [viewYear, setViewYear] = useState(init.getUTCFullYear());
  const [viewMonth, setViewMonth] = useState(init.getUTCMonth()); // 0..11

  // При открытии — загрузка доступных дат
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    setStep(1);
    setSelectedDateUTC('');
    setSelectedTimeUTC('');

    mainApi.getBookedDates()
      .then(res => {
        const set = new Set();
        const remains = {};
        if (res && Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (!item?.date) return;
            // item.date: "YYYY-MM-DDTHH:mm:00" — берём дату
            const [ymd] = String(item.date).split('T');
            if (!ymd) return;
            // фильтр по прошлому/слишком дальнему — на всякий
            const dMs = epochMsFromUTC(ymd, '00:00');
            if (dMs < nowMs) return;

            // считаем датой доступной, если remain_count > 0
            const remain = Number(item.remain_count ?? 0);
            if (remain > 0) {
              set.add(ymd);
              remains[ymd] = remain;
            }
          });
        }
        setAvailableDateSet(set);
        setRemainByDate(remains);

        // Подстроим месяц показа под ближайшую доступную дату (если она не в текущем)
        if (set.size > 0) {
          const nearest = [...set].sort((a, b) => epochMsFromUTC(a, '00:00') - epochMsFromUTC(b, '00:00'))[0];
          const [Y, M] = nearest.split('-').map(Number);
          setViewYear(Y);
          setViewMonth(M - 1);
        }
      })
      .catch(err => console.log('getBookedDates error:', err));

    return () => { document.body.style.overflow = 'initial'; };
  }, [isOpen]);

  // сетка дней календаря (только доступные кликабельны)
  const daysGrid = useMemo(() => {
    const { weekdayMonFirst, daysInMonth } = getMonthMetaUTC(viewYear, viewMonth);
    const leadingEmpty = (weekdayMonFirst - 1);
    const totalCells = Math.ceil((leadingEmpty + daysInMonth) / 7) * 7;
    const cells = [];

    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - leadingEmpty + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        cells.push({ type: 'blank', key: `b${i}` });
        continue;
      }
      const dateUTC = new Date(Date.UTC(viewYear, viewMonth, dayNum));
      const ymd = ymdUTC(dateUTC);
      const isAvailable = availableDateSet.has(ymd);
      const isSelected = selectedDateUTC === ymd;
      const isToday = ymd === todayUTC;

      cells.push({
        type: 'day',
        key: `d${ymd}`,
        ymd,
        dayNum,
        isAvailable,
        isSelected,
        isToday,
        remain: remainByDate[ymd] ?? 0,
      });
    }
    return cells;
  }, [viewYear, viewMonth, selectedDateUTC, availableDateSet, remainByDate, todayUTC]);

  function goPrevMonth() {
    const prev = new Date(Date.UTC(viewYear, viewMonth, 1));
    prev.setUTCMonth(prev.getUTCMonth() - 1);
    setViewYear(prev.getUTCFullYear());
    setViewMonth(prev.getUTCMonth());
  }
  function goNextMonth() {
    const next = new Date(Date.UTC(viewYear, viewMonth, 1));
    next.setUTCMonth(next.getUTCMonth() + 1);
    setViewYear(next.getUTCFullYear());
    setViewMonth(next.getUTCMonth());
  }

  /** ===== Слоты на выбранный день ===== */
  const [slotListUTC, setSlotListUTC] = useState([]); // [{ time:'HH:mm', is_available:bool, inPast:bool }]

  // загрузка свободных слотов по выбранной дате
  useEffect(() => {
    if (!selectedDateUTC) {
      setSlotListUTC([]);
      return;
    }

  }, [selectedDateUTC, nowMs]);

  // доступность кнопки time
  const canSubmitFromTime = useMemo(() => {
    if (!selectedTimeUTC) return false;
    const slot = slotListUTC.find(s => s.time === selectedTimeUTC);
    return !!slot && slot.is_available && !slot.inPast;
  }, [selectedTimeUTC, slotListUTC]);

  // степ 1 валиден?
  const canGoNextFromPersonal =
    !!personal.first_name &&
    !!personal.phone && personalValidity.phone.validState &&
    !!personal.email && personalValidity.email.validState;

  const canGoNextFromDate = !!selectedDateUTC;

  /** ===== Submit: отправляем UTC-наивное ===== */
  function Submit() {
    const payload = {
      name: personal.first_name,
      email: personal.email,
      phone: personal.phone.replace(/\D/g, ''),
    };
    if (selectedDateUTC && selectedTimeUTC) {
      payload.appointment_date = buildAppointmentDateUTC(selectedDateUTC, selectedTimeUTC);
    }
    mainApi.sendRequestContact(payload)
      .then(() => {
        setCorrectSending(true); // ← только показываем success
        // НИЧЕГО НЕ СБРАСЫВАЕМ ЗДЕСЬ
      })
      .catch((error) => {
        console.error('sendRequestContact error', error);
        setCorrectSending(false);
      });
  }

  function closePopup() {
    setOpen(false);
    setTimeout(() => {
      setCorrectSending(false);
      setPersonal({ first_name: '', phone: '', email: '' });
      setPersonalValidity({
        phone: { validState: false, errorMessage: '' },
        email: { validState: false, errorMessage: '' },
      });
      setSelectedDateUTC('');
      setSelectedTimeUTC('');
      setSlotListUTC([]);
      setStep(1);
    }, 300);
  }


  function selectDate(date) {
    setSelectedDateUTC(date);
    setSelectedTimeUTC('')
    mainApi.getFreeSlotsByDate({ appointment_date: date })
      .then(res => {
        const out = [];
        if (res && Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (!item?.date) return;
            const [ymd, hms] = String(item.date).split('T'); // e.g. 2025-09-11T08:00:00
            console.log({ ymd, hms, bool: ymd !== selectedDateUTC, selectedDateUTC })
            if (!hms) return;
            const [hh, mm] = hms.split(':').map(Number);
            const time = `${pad(hh)}:${pad(mm)}`;
            const inPast = epochMsFromUTC(selectedDateUTC, time) < nowMs;
            out.push({
              time, // UTC
              is_available: Boolean(item.is_available),
              inPast,
            });
          });
        }
        // сортируем по времени
        out.sort((a, b) => a.time.localeCompare(b.time));
        console.log({ res, out })
        setSlotListUTC(out);
      })
      .catch(err => {
        console.log('getFreeSlotsByDate error:', err);
        setSlotListUTC([]);
      });
  }

  const storeData = useMemo(
    () => DELIVERIES?.find(item => item.delivery_type === NO_DELIVERY_TYPE)?.data || {},
    [DELIVERIES]
  );
  const storeAddress = storeData?.store_address || 'Dubai, Office Address';
  const storeCoords = storeData?.coords;

  const selectedDateLabel = selectedDateUTC ? moment(selectedDateUTC).format('DD.MM.YYYY') : '';
  const selectedTimeLabel = selectedTimeUTC
    ? (() => { const [hh, mm] = selectedTimeUTC.split(':').map(Number); return utcTimeToUserLabel(hh, mm); })()
    : '';


  function sendPrebooking() {
    const payload = {
      name: personal.first_name,
      email: personal.email,
      phone: personal.phone.replace(/\D/g, ''),
      message: 'Start booking appointment',
    };
    mainApi.sendRequestContact(payload)
      .then(() => {
      })
      .catch((error) => {
        console.error('send Pre Contact error', error);
      });
  }
  return (
    <Popup className="call-back-popup" popupName={'call-back-popup'} onClose={closePopup} {...{ setOpen, isOpen }}>
      {!correctSending ? (
        <>
          <button className="call-back-popup__close-btn" type="button" onClick={closePopup}>
            <ClosePopupIcon
              mainClassName="call-back-popup__close-icon"
              strokeClassName="call-back-popup__close-icon-stroke"
            />
          </button>

          <div className="call-back-popup__content">
            <p className="call-back-popup__title">Book an appointment</p>
            {step === 1 && (
              <p className="call-back-popup__subtitle" aria-live="polite">
                Booking is for our Dubai office. All times are shown in Dubai time (UTC+4).
                {' '}Address: <strong>{storeAddress}</strong>
                {Array.isArray(storeCoords) && storeCoords.length === 2 && (
                  <>
                    {' '}·{' '}
                    <a
                      href={`https://www.google.com/maps?q=${storeCoords[1]},${storeCoords[0]}`}
                      target="_blank"
                      rel="noreferrer"
                      className='call-back-popup__map-link'
                    >
                      Open on map
                    </a>
                  </>
                )}
              </p>
            )}
            {/* Stepper */}
            {/* <div className="cbp-steps" aria-label="Steps">
              <div className={`cbp-step ${step === 1 ? 'cbp-step_active' : ''}`}>1 · Details</div>
              <div className={`cbp-step ${step === 2 ? 'cbp-step_active' : ''}`}>2 · Date</div>
              <div className={`cbp-step ${step === 3 ? 'cbp-step_active' : ''}`}>3 · Time</div>
            </div> */}

            {/* STEP 1: PERSONAL */}
            {step === 1 && (
              <>
                <div className='call-back-popup__inputs-box'>
                  <div className="personal-info__input">
                    <Input label="Name" value={personal.first_name} handleChange={handleChange} name="first_name" />
                  </div>
                  <div className="personal-info__input">
                    <Input
                      label="Phone Number"
                      value={personal.phone}
                      handleChange={handleChange}
                      name="phone"
                      inputMode="tel"
                      error={personalValidity.phone}
                      onKeyDown={handlePhoneDelete}
                    />
                  </div>
                  <div className="personal-info__input">
                    <Input
                      label="E-mail"
                      value={personal.email}
                      handleChange={handleChange}
                      name="email"
                      inputMode="email"
                      error={personalValidity.email}
                    />
                  </div>
                </div>

                <div className="cbp-actions">
                  <button
                    className={`call-back-popup__btn-submit ${!canGoNextFromPersonal ? 'call-back-popup__btn-submit_invalid' : ''}`}
                    onClick={() => {
                      setStep(2)
                      sendPrebooking()
                    }}
                  >
                    Continue
                  </button>
                </div>

                <div className='cbp-inline-contacts'>
                  <a className='cbp-inline-contacts__link' href={`tel:${CONTACT_PHONE.value}`}>{CONTACT_PHONE.formated}</a>
                  {/* <span>·</span>
                  <a className='cbp-inline-contacts__link' href={`tel:${CONTACT_PHONE_2.value}`}>{CONTACT_PHONE_2.formated}</a> */}
                </div>
              </>
            )}

            {/* STEP 2: DATE — календарь по getBookedDates */}
            {step === 2 && (
              <div className="cbp-calendar">
                <div className="cbp-calendar__header">
                  <button className="cbp-nav" onClick={goPrevMonth} aria-label="Previous month">‹</button>
                  <div className="cbp-calendar__title">{monthTitleUTC(viewYear, viewMonth)}</div>
                  <button className="cbp-nav" onClick={goNextMonth} aria-label="Next month">›</button>
                </div>

                <div className="cbp-calendar__weekdays">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(w => (
                    <div key={w} className="cbp-calendar__weekday">{w}</div>
                  ))}
                </div>

                <div className="cbp-calendar__grid">
                  {daysGrid.map(cell => {
                    if (cell.type === 'blank') return <div key={cell.key} className="cbp-calendar__cell cbp-calendar__cell_blank" />;
                    const { ymd, dayNum, isAvailable, isSelected, isToday, remain } = cell;
                    const dotMods =
                      remain === 1
                        ? ' cbp-calendar__dot--red'
                        : (remain > 1 && remain <= LOW_STOCK_THRESHOLD ? ' cbp-calendar__dot--yellow' : '');

                    return (
                      <button
                        key={cell.key}
                        className={`cbp-calendar__cell ${isSelected ? 'cbp-calendar__cell_selected' : ''} ${isToday ? 'cbp-calendar__cell_today' : ''}`}
                        disabled={!isAvailable}
                        onClick={() => { selectDate(ymd); }}
                        aria-pressed={isSelected}
                        aria-label={`${ymd}${remain > 0 ? `, ${remain} slots left` : ''}`}
                      >
                        <span>{dayNum}</span>
                        {remain > 0 && <span className={`cbp-calendar__dot${dotMods}`} />}
                      </button>
                    );
                  })}
                </div>

                <div className="cbp-actions">
                  <button className="cbp-secondary" onClick={() => setStep(1)}>Back</button>
                  <button
                    className={`call-back-popup__btn-submit ${!canGoNextFromDate ? 'call-back-popup__btn-submit_invalid' : ''}`}
                    onClick={() => setStep(3)}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: TIME — слоты из getFreeSlotsByDate (UTC), показ в UTC+4 */}
            {step === 3 && (
              <div className="cbp-time">
                <div className="cbp-time__top">
                  <button className="cbp-backlink" onClick={() => setStep(2)} aria-label="Back to date">← {moment(selectedDateUTC).format('DD.MM.YYYY')}</button>
                  <span className="cbp-time__note">Times shown in Dubai time (UTC+4)</span>
                </div>

                <div className="cbp-time-grid">
                  {slotListUTC.map(slot => {
                    const [hh, mm] = slot.time.split(':').map(Number);
                    const label = utcTimeToUserLabel(hh, mm); // показываем +4
                    const disabled = !slot.is_available || slot.inPast;
                    return (
                      <button
                        key={slot.time}
                        type="button"
                        className={`cbp-time-slot ${selectedTimeUTC === slot.time ? 'cbp-time-slot_active' : ''}`}
                        disabled={disabled}
                        onClick={() => setSelectedTimeUTC(slot.time)}
                        aria-pressed={selectedTimeUTC === slot.time}
                        title={disabled ? 'Unavailable' : 'Available'}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <div className="cbp-actions">
                  <button className="cbp-secondary" onClick={() => setStep(2)}>Back</button>
                  <button
                    onClick={Submit}
                    className={`call-back-popup__btn-submit ${!canSubmitFromTime ? 'call-back-popup__btn-submit_invalid' : ''}`}
                  >
                    Book
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='call-back-popup__submit-send-box'>
          <p className='call-back-popup__submit-send-box__title'>Request submitted</p>
          <p className='call-back-popup__submit-send-box__text'>
            Thank you! We’ll confirm your appointment soon.
          </p>
          {(selectedDateLabel && selectedTimeLabel) && (
            <p className='call-back-popup__submit-send-box__text'>
              Your booking: <strong>{selectedDateLabel} at {selectedTimeLabel} (UTC+4)</strong>
            </p>
          )}
          <p className='call-back-popup__submit-send-box__text'>
            Meeting location: <strong>{storeAddress}</strong>
            {Array.isArray(storeCoords) && storeCoords.length === 2 && (
              <>
                {' '}·{' '}
                <a
                  href={`https://www.google.com/maps?q=${storeCoords[1]},${storeCoords[0]}`}
                  target="_blank"
                  rel="noreferrer"
                  className='call-back-popup__map-link'
                >
                  Open on map
                </a>
              </>
            )}
          </p>
          <button onClick={closePopup} className='call-back-popup__submit-send-box__btn'>OK</button>
        </motion.div>
      )}
    </Popup>
  );
}
